import { Location } from '@angular/common';
import { Component, NgZone, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { format, startOfDay } from 'date-fns';
import getDay from 'date-fns/getDay';
import { BehaviorSubject, firstValueFrom, map, Subject, takeUntil } from 'rxjs';
import { randomId } from 'server/shared/helpers/random';
import { Certificate } from 'server/shared/models/certificate';
import {
  CustomCriteria,
  CustomCriteriaOnJobSelect,
  CustomCriteriaOnJobToggle,
  CustomCriteriaType,
} from 'server/shared/models/customCriteria';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { CustomerInternalInformation, Job } from 'server/shared/models/job';
import { JobRequestCreateInput } from 'server/shared/models/job-request';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { PoolerType } from 'server/shared/models/poolerType';
import { SexEnum } from 'server/shared/models/sex.enum';
import { Shift } from 'server/shared/models/shift';
import {
  getWeekDayEnumByIndex,
  WeekDayEnum,
} from 'server/shared/models/weekday.enum';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { queryParam$ } from '../../../helpers/queryParam$';
import { CustomerService } from '../../services/customer.service';
import { JobRequestService } from '../../services/job-request.service';
import { OrganisationService } from '../../services/organisation.service';
import { ToastService } from '../../services/toast/toast.service';
import { getDatesFromRanges } from '../../shared/date-range-select/getDatesFromRanges';
import { DateRange } from '../../shared/date-ranges-input/date-ranges-input.component';

type JobRequestMultiDay = {
  enabled: boolean;
  date: Date;
  dateString: string;
  shifts: Shift[];
  jobType?: JobTypeEnum;
  // availableDepartmentShifts should not be modified, modify & use "shifts" instead
  availableDepartmentShifts: Shift[];
};

@Component({
  selector: 'app-job-request-edit',
  templateUrl: './job-request-edit.component.html',
  styleUrls: ['./job-request-edit.component.scss'],
})
export class JobRequestEditComponent implements OnDestroy {
  // should prevent multiple submits creating the same job requests multiple times when double-clicking
  isSubmitting = false;

  multiDays: JobRequestMultiDay[] = [];

  jobTypeControl = new FormControl(JobTypeEnum.NORMAL, Validators.required);
  dateRangesControl = new FormControl([], Validators.required);
  demandReasonControl = new FormControl(null, Validators.required);
  demandReasonDescriptionControl = new FormControl('');
  departmentIdControl = new FormControl(null);
  descriptionControl = new FormControl('');
  caseNumberControl = new FormControl('');
  sexControl = new FormControl(null);
  certificatesControl = new FormControl([]);
  specialitiesControl = new FormControl([]);
  poolerTypesControl = new FormControl([]);
  internalDescriptionControl = new FormControl('');

  jobRequestForm = new FormGroup({
    jobType: this.jobTypeControl,
    dateRanges: this.dateRangesControl,
    demandReason: this.demandReasonControl,
    demandReasonDescription: this.demandReasonDescriptionControl,
    departmentId: this.departmentIdControl,
    description: this.descriptionControl,
    caseNumber: this.caseNumberControl,
    sex: this.sexControl,
    certificates: this.certificatesControl,
    specialities: this.specialitiesControl,
    poolerTypes: this.poolerTypesControl,
    internalDescription: this.internalDescriptionControl,
  });

  customer$ = this.customerService.readyDispoCustomer$;

  jobRequestCustomCriteria$ = this.customer$.pipe(
    map((c) =>
      (c?.customCriteria ?? []).filter((cc) => cc.enableOnJobRequest !== false),
    ),
  );

  customerInternalInformation: CustomerInternalInformation =
    {} as CustomerInternalInformation;

  private loggedInCustomer?: Customer;

  readonly customCriteriaTypeEnum = CustomCriteriaType;

  compareCustomCriteriaOption = (
    a: { id: string },
    b: { id: string },
  ): boolean => !!a && !!b && a.id === b.id;

  hideDescription$ = this.customer$.pipe(
    map((c) => !!c.hiddenFields?.jobDescription),
  );
  hideSex$ = this.customer$.pipe(map((c) => !!c.hiddenFields?.jobSex));
  hideCertificates$ = this.customer$.pipe(
    map((c) => !!c.hiddenFields?.jobCertificates),
  );
  certificatesRequired$ = this.customer$.pipe(
    map((c) => !!c.requiredJobFields?.certificates),
  );
  hidePoolerTypes$ = this.customer$.pipe(
    map((c) => !c.enablePoolerTypes || !!c.hiddenFields?.jobPoolerTypes),
  );

  specialitiesVisible$ = this.customer$.pipe(
    map((c) => !!c.enableSpecialitiesForJobRequests),
  );

  enableCaseNumberInJobRequests$ = this.customer$.pipe(
    map((c) => !!c.enableCaseNumberInJobRequests),
  );

  hideJobType$ = this.customer$.pipe(map((c) => !!c.hiddenFields?.jobtype));

  dateRanges: DateRange[] = [];

  shiftParam$ = queryParam$(this.route, 'shift');
  shiftIdParam$ = queryParam$(this.route, 'shiftId');

  shiftJobTypes?: JobTypeEnum[];
  poolerTypes?: PoolerType[];

  @Output() createJobRequest = new BehaviorSubject<Job | undefined>(undefined);

  private destroyed$ = new Subject<void>();

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private organisationService: OrganisationService,
    private toastService: ToastService,
    private jobRequestService: JobRequestService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.loggedInCustomer = customer;
        this.poolerTypes = customer.poolerTypes || [];
        this.mergeCustomerCustomCriteriaIntoJobRequestInternalInfo();

        let departmentId = this.route.snapshot.queryParams['departmentId'];
        let defaultValues = customer.defaultJobValues?.find((defaultValues) => {
          return defaultValues.department.id === departmentId;
        });
        if (defaultValues) {
          this.jobRequestForm.patchValue({
            description: defaultValues.values?.description,
            certificates: defaultValues.values?.certificates,
            specialities: defaultValues.values?.specialities,
            poolerTypes: defaultValues.values?.poolerTypes?.map(
              (poolerType) => poolerType.id,
            ),
          });
        }
      });
    const jobRequestId = route.snapshot.params['jobRequestId'];
    if (jobRequestId === 'new') {
      this.initFormData(route.snapshot.queryParams);
      this.updateMultiDays().then();
    } else {
      console.log('Edit Job request by ID not supported yet');
    }
    this.certificatesRequired$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((required) => {
        if (required) {
          this.certificatesControl.setValidators(Validators.required);
        } else {
          this.certificatesControl.clearValidators();
        }
        this.certificatesControl.updateValueAndValidity();
      });
  }

  initFormData(queryParams: Params) {
    this.jobRequestForm.patchValue({
      jobType: JobTypeEnum.NORMAL,
      dateRanges: [
        {
          from: startOfDay(
            queryParams['date'] ? new Date(queryParams['date']) : new Date(),
          ),
        },
      ],
      departmentId: queryParams['departmentId'],
    });
  }

  async createJobRequests() {
    // isSubmitting is not reset because the user should not stay in the form
    // which could lead to the same requests being created multiple times
    this.isSubmitting = true;

    if (!this.jobRequestForm.valid) {
      this.jobRequestForm.markAllAsTouched();
      this.toastService.add({ type: 'INFO', message: 'InvalidForm' });
      this.isSubmitting = false;
      return;
    }
    const result: {
      success: Job[];
      errors: string[];
    } = {
      success: [],
      errors: [],
    };

    // Generate a single multiDayId for all job requests if creating multiple jobs
    const enabledDays = this.multiDays.filter((day) => day.enabled);
    const multiDayId = enabledDays.length > 1 ? randomId() : undefined;

    for (const day of enabledDays) {
      const jobRequest: JobRequestCreateInput = {
        jobType: day.jobType ?? this.jobTypeControl.value!, // Use day.jobType, fallback to main
        date: day.dateString,
        reason: this.demandReasonControl.value!,
        reasonDescription:
          this.demandReasonDescriptionControl.value || undefined,
        description: this.descriptionControl.value || undefined,
        caseNumber: this.caseNumberControl.value || undefined,
        internalDescription: this.internalDescriptionControl.value || undefined,
        sex:
          this.sexControl.value || this.sexControl.value === 0
            ? this.sexControl.value
            : undefined,
        certificates: this.certificatesControl.value || undefined,
        specialities: this.specialitiesControl.value || undefined,
        poolerTypes:
          this.poolerTypesControl.value?.length > 0
            ? this.poolerTypes?.filter((poolerType) =>
                this.poolerTypesControl.value?.includes(poolerType.id),
              )
            : undefined,
        shifts: day.shifts,
        departmentId: this.departmentIdControl.value!,
        multiDayId: multiDayId,
        ...(this.customerInternalInformation.customCriteria
          ? { customCriteria: this.customerInternalInformation.customCriteria }
          : {}),
      };
      try {
        result.success.push(
          await this.jobRequestService.createJobRequest(jobRequest),
        );
      } catch (e: any) {
        result.errors.push(e?.message || e.toString());
      }
    }
    if (result.success.length === 1) {
      this.toastService.add({
        type: 'SUCCESS',
        message: 'newJobRequestCreated',
      });
    } else if (result.success.length > 1) {
      this.toastService.add({
        type: 'SUCCESS',
        message: 'xNewJobRequestsCreated',
        messageOptions: {
          count: result.success.length.toString(),
        },
      });
    }
    result.errors.forEach((error) => {
      this.toastService.add({ type: 'ERROR', message: error });
    });
    this.historyBack();
  }

  selectDepartment(department: Department | undefined) {
    this.departmentIdControl.setValue(department?.id);
  }

  readonly sexEnum = SexEnum;

  certificatesSelected(certificates: Certificate[]) {
    this.certificatesControl.setValue(
      certificates.map((certificate) => ({
        id: certificate._id,
        displayName: certificate.name,
      })),
    );
  }

  specialitiesSelected(specialities: Certificate[]) {
    this.specialitiesControl.setValue(
      specialities.map((speciality) => ({
        id: speciality._id,
        displayName: speciality.name,
      })),
    );
  }

  poolerTypesSelected(poolerTypeIds: string[]) {
    this.poolerTypesControl.setValue(poolerTypeIds);
  }

  dateRangesChanged(newDateRangesData: { dateRanges: DateRange[] }) {
    this.dateRangesControl.setValue(newDateRangesData.dateRanges);
    this.updateMultiDays().then();
  }

  jobTypeSelected() {
    this.updateMultiDays().then();
  }

  async updateMultiDays() {
    const desiredJobType = this.jobTypeControl.value;
    const departmentId = this.departmentIdControl.value;
    if (!departmentId) {
      this.multiDays = [];
      return;
    }

    const result =
      await this.organisationService.getOrganisationAndDepartmentById(
        departmentId,
      );
    const paramShift = await firstValueFrom(this.shiftParam$);
    const paramShiftId = await firstValueFrom(this.shiftIdParam$);
    if (!paramShift) {
      console.error('missing shift param');
      return;
    }

    let shift: Shift | undefined;
    if (paramShiftId) {
      shift = result?.department?.shifts.find((s) => s.id === paramShiftId);
    }
    if (!shift) {
      shift = result?.department?.shifts.find(
        (s) => s.shift === Number(paramShift),
      );
    }
    if (!shift) {
      console.error('shift not found on department');
      return;
    }
    this.shiftJobTypes = shift.specificJobTypes;
    const jobType =
      !shift.specificJobTypes ||
      shift.specificJobTypes?.includes(desiredJobType)
        ? desiredJobType
        : shift.specificJobTypes?.[0] ?? JobTypeEnum.NORMAL;

    this.jobTypeControl.setValue(jobType);

    const dates = getDatesFromRanges(this.dateRangesControl.value);

    let departmentShifts = result?.department?.shifts || [];

    this.multiDays = dates.map((date) => ({
      enabled: true,
      date,
      dateString: format(date, dbDayDateToken),
      shifts: [shift!],
      jobType,
      availableDepartmentShifts: this.getShiftsForWeekday(
        departmentShifts,
        getWeekDayEnumByIndex(getDay(date)),
      ),
    }));
  }

  getShiftsForWeekday(
    departmentShifts: Shift[],
    weekDay: WeekDayEnum,
  ): Shift[] {
    return (
      departmentShifts
        .filter((shift) => {
          return (
            !shift.specificWeekDays ||
            shift.specificWeekDays.length === 0 ||
            shift.specificWeekDays?.includes(weekDay)
          );
        })
        .sort((a, b) => a.from - b.from) || []
    );
  }

  historyBack() {
    this.location.back();
  }

  toggleDayEnabled(day: JobRequestMultiDay) {
    day.enabled = !day.enabled;
  }

  editMultiDayShifts(
    day: JobRequestMultiDay,
    eventData: { shifts: Shift[]; jobType?: JobTypeEnum },
  ) {
    day.shifts = eventData.shifts;
    day.jobType = eventData.jobType;
  }

  jobTypeEnum = JobTypeEnum;

  trackByCustomCriteriaId(_index: number, def: CustomCriteria): string {
    return def.id;
  }

  getJobSingleSelectValue(
    criterion: CustomCriteriaOnJobSelect,
  ): { id: string; displayName: string } | undefined {
    return criterion.values?.[0];
  }

  setJobSingleSelectValue(
    criterion: CustomCriteriaOnJobSelect,
    opt: { id: string; displayName: string } | null | undefined,
  ): void {
    criterion.values = opt ? [opt] : [];
  }

  jobToggleCriterion(criteriaId: string): CustomCriteriaOnJobToggle {
    return this.customerInternalInformation.customCriteria![
      criteriaId
    ] as CustomCriteriaOnJobToggle;
  }

  jobSelectCriterion(criteriaId: string): CustomCriteriaOnJobSelect {
    return this.customerInternalInformation.customCriteria![
      criteriaId
    ] as CustomCriteriaOnJobSelect;
  }

  private mergeCustomerCustomCriteriaIntoJobRequestInternalInfo(): void {
    if (!this.loggedInCustomer) return;
    const info = this.customerInternalInformation;
    const customerCustomCriterias =
      this.loggedInCustomer.customCriteria?.filter(
        (c) => c.enableOnJobRequest !== false,
      );
    if (!customerCustomCriterias?.length) {
      info.customCriteria = undefined;
      return;
    }

    const existingCustomCriterias = info.customCriteria || {};
    const newCustomCriteria: NonNullable<
      CustomerInternalInformation['customCriteria']
    > = {};

    customerCustomCriterias.forEach((customerCustomCriteria) => {
      const existingCustomCriteria =
        existingCustomCriterias[customerCustomCriteria.id];
      if (customerCustomCriteria.type === CustomCriteriaType.TOGGLE) {
        if (existingCustomCriteria?.type === CustomCriteriaType.TOGGLE) {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.TOGGLE,
            value: existingCustomCriteria.value,
            // always get default value from customer
            defaultValueOnJob: customerCustomCriteria.defaultValueOnJob,
            defaultValueOnProfile: customerCustomCriteria.defaultValueOnProfile,
            blockingToggle: customerCustomCriteria.blockingToggle,
          };
        } else {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.TOGGLE,
            value: customerCustomCriteria.defaultValueOnJob,
            defaultValueOnJob: customerCustomCriteria.defaultValueOnJob,
            defaultValueOnProfile: customerCustomCriteria.defaultValueOnProfile,
            blockingToggle: customerCustomCriteria.blockingToggle,
          };
        }
      } else {
        if (existingCustomCriteria?.type === CustomCriteriaType.SELECT) {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.SELECT,
            values: existingCustomCriteria.values,
          };
        } else {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.SELECT,
            values: [],
          };
        }
      }
    });

    info.customCriteria = newCustomCriteria;
  }

  jobCustomCriteriaChanged(): void {}

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
